"use client";

import { Configure, InstantSearch, useHits } from "react-instantsearch";
import type { ConfigureProps } from "react-instantsearch";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { SearchContextProvider } from "@/components/Search/components/SearchContextProvider";
import { getFacetFiltersValue } from "@/components/Search/utils/getFacetFiltersValue";
import {
  DEFAULT_SEARCH_CONFIG,
  QUERY_URL_PARAM,
  RESULT_PRODUCT_TYPE_MAP,
} from "@/components/Search/constants";
import getFilters from "@/components/Search/utils/getFilters";
import { getMappedSearchParams } from "@/components/Search/utils/mapFacetParams";
import useSearchInitialState from "@/components/Search/hooks/useSearchInitialState";
import getResultTypeFromUrl from "@/components/Search/utils/getResultTypeFromUrl";
import { PRODUCT_SUMMARY_INDEX_EN } from "@/constants/algolia";
import getPublicClient from "@/lib/algolia/getPublicClient";
import { ProductSummaryWithHighlight } from "../ProductSearch/types";
import type {
  BaseCompactProductListProps,
  CompactProductListSearchWrapperProps,
} from "./types";
import { convertHitToCompactProduct } from "./utils/convertHitToCompactProduct";
import { CompactProductList } from ".";

const DEFAULT_NUMBER_OF_HITS = 3;
const DEFAULT_CTA_LABEL = "See all";

const searchClient = getPublicClient();

interface CompactProductListSearchProps extends BaseCompactProductListProps {
  numberOfHits: number;
  ctaUrl: string;
  ctaLabel: string;
}

const CompactProductListSearch = ({
  title,
  numberOfHits,
  ctaUrl,
  ctaLabel,
  ctaHtmlId,
  location,
}: CompactProductListSearchProps) => {
  const { items: hits } = useHits<ProductSummaryWithHighlight>();

  const products = hits.slice(0, numberOfHits).map(convertHitToCompactProduct);

  if (products.length === 0) {
    return null;
  }

  return (
    <CompactProductList
      title={title}
      ctaUrl={ctaUrl}
      ctaLabel={ctaLabel}
      ctaHtmlId={ctaHtmlId}
      products={products}
      location={location}
    />
  );
};

export const CompactProductListSearchWrapper = ({
  query,
  title,
  numberOfHits = DEFAULT_NUMBER_OF_HITS,
  ctaUrl,
  ctaLabel = DEFAULT_CTA_LABEL,
  ctaHtmlId,
  location,
}: CompactProductListSearchWrapperProps) => {
  const indexName = PRODUCT_SUMMARY_INDEX_EN as string;

  const searchParams = new URLSearchParams(query);
  const searchQuery = searchParams.get(QUERY_URL_PARAM);
  const { paramsArray } = getMappedSearchParams(searchParams, indexName);
  const pageTab =
    RESULT_PRODUCT_TYPE_MAP[
      getResultTypeFromUrl(searchParams as unknown as ReadonlyURLSearchParams)
    ];
  const initialSearchState = useSearchInitialState(indexName);

  const configForSearch: ConfigureProps = {
    ...DEFAULT_SEARCH_CONFIG,
    filters: getFilters({ indexName }),
    query: searchQuery || "",
    facetFilters: getFacetFiltersValue({ indexName, paramsArray }),
    hitsPerPage: numberOfHits,
  };

  if (pageTab) {
    configForSearch.facetsRefinements = { product: [pageTab] };
  }

  const resolvedCtaUrl =
    ctaUrl ?? `/search${query.startsWith("?") ? query : `?${query}`}`;

  return (
    <SearchContextProvider searchIndex={indexName}>
      <InstantSearch
        searchClient={searchClient}
        indexName={indexName}
        initialUiState={initialSearchState}
        future={{ preserveSharedStateOnUnmount: true }}
      >
        <Configure {...configForSearch} />
        <CompactProductListSearch
          title={title}
          numberOfHits={numberOfHits}
          ctaUrl={resolvedCtaUrl}
          ctaLabel={ctaLabel}
          ctaHtmlId={ctaHtmlId}
          location={location}
        />
      </InstantSearch>
    </SearchContextProvider>
  );
};
